/**
 * Recover from Next.js / Turbopack ChunkLoadError after a deploy.
 * Stale tabs keep an old runtime that requests hashed chunks that no longer
 * exist on the CDN; a one-shot full reload picks up the new HTML + chunk map.
 */

export const CHUNK_LOAD_RELOAD_KEY = "mple:chunk-load-reload";

/**
 * Caps reload attempts within a single page lifetime (concurrent ChunkLoadErrors
 * before navigation completes). Persistence across a full reload still depends on
 * sessionStorage; if storage throws we skip recovery instead of looping forever.
 */
let reloadAttempted = false;

export const isChunkLoadError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;

  const name = "name" in error ? String(error.name ?? "") : "";
  const message = "message" in error ? String(error.message ?? "") : "";
  const combined = `${name} ${message}`;

  return (
    name === "ChunkLoadError" ||
    combined.includes("Failed to load chunk") ||
    combined.includes("Loading chunk")
  );
};

/** Clear the reload guard after a successful boot so a later deploy can recover again. */
export const clearChunkLoadReloadFlag = (): void => {
  try {
    sessionStorage.removeItem(CHUNK_LOAD_RELOAD_KEY);
  } catch {
    // sessionStorage may be unavailable (private mode / blocked)
  }
};

/**
 * Whether PostHog should drop a ChunkLoadError-class `$exception`.
 * Drop only the recoverable first-shot (or in-flight reload) case.
 * Keep repeats after the one-shot guard is already set — those are actionable
 * (genuinely missing chunk / broken deploy).
 */
export const shouldSuppressChunkLoadException = (): boolean => {
  if (typeof window === "undefined") return false;
  // Offline: we do not recover — keep the exception visible.
  if (navigator.onLine === false) return false;
  // We triggered a reload this page lifetime — that exception is noise.
  if (reloadAttempted) return true;

  try {
    // Guard already consumed → persistent failure after recovery → keep it.
    if (sessionStorage.getItem(CHUNK_LOAD_RELOAD_KEY) === "1") {
      return false;
    }
  } catch {
    // Cannot read the guard — do not hide the error.
    return false;
  }

  // First-shot: handlers will recover — suppress the noise.
  return true;
};

/**
 * @returns true if a reload was triggered (caller should not show error UI).
 * @returns false if recovery is skipped — fall through to Oops UI.
 */
export const recoverFromChunkLoadError = (): boolean => {
  if (typeof window === "undefined") return false;
  if (reloadAttempted) return false;
  // Chunk fetches also fail offline; reloading swaps our Oops/Retry UI for the
  // browser's native offline page — worse UX. Skip and let the error UI show.
  if (navigator.onLine === false) return false;

  try {
    if (sessionStorage.getItem(CHUNK_LOAD_RELOAD_KEY) === "1") {
      return false;
    }
    sessionStorage.setItem(CHUNK_LOAD_RELOAD_KEY, "1");
  } catch {
    // Without persistent storage we cannot cap reloads across navigations.
    // Skip recovery rather than risk an infinite reload loop on a broken deploy.
    // `reloadAttempted` still blocks concurrent attempts this page lifetime.
    return false;
  }

  reloadAttempted = true;
  window.location.reload();
  return true;
};
